// ===========================================
// Program Levels Management Page
// ===========================================
// CRUD interface for managing program levels with drag-and-drop reordering

import { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Typography,
  Skeleton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Folder as FolderIcon,
  EmojiEvents as AwardIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PageHeader from '../../components/common/PageHeader';
import FormModal from '../../components/common/FormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ColorPickerField from '../../components/common/ColorPickerField';
import {
  useProgramLevels,
  useCreateProgramLevel,
  useUpdateProgramLevel,
  useDeleteProgramLevel,
  useReorderProgramLevels,
} from '../../hooks/useAdmin';
import { ProgramLevel } from '../../api/admin.api';
import DriveFolderPicker from '../../components/googleDrive/DriveFolderPicker';
import { useGoogleDriveAuthStatus } from '../../hooks/useGoogleDrive';

// ===========================================
// AVATAR DATA
// ===========================================

const MASCOT_CHARACTERS = [
  { name: 'Alice', folder: 'alice' },
  { name: 'Steve', folder: 'steve' },
  { name: 'Liam', folder: 'liam' },
  { name: 'Floyd', folder: 'floyd' },
] as const;

const MASCOT_POSES = [
  'happy', 'standing', 'piano', 'guitar', 'drums', 'microphone', 'singing',
] as const;

/** Extra poses only some characters have */
const EXTRA_POSES: Record<string, string[]> = {
  alice: ['maracas'],
  steve: ['walking'],
  liam: ['walking'],
  floyd: ['walking'],
};

function getAvatarPath(folder: string, pose: string): string {
  return `/avatars/${folder}/${folder}-${pose}.svg`;
}

function getAllPoses(folder: string): string[] {
  const poses = [...MASCOT_POSES] as string[];
  const extras = EXTRA_POSES[folder];
  if (extras) poses.push(...extras);
  return poses;
}

/** Parse a stored mascotEmoji path back to character + pose */
function parseAvatarPath(path: string | null): { character: string; pose: string } | null {
  if (!path || !path.startsWith('/avatars/')) return null;
  const match = path.match(/\/avatars\/(\w+)\/\w+-(\w+)\.svg$/);
  if (!match) return null;
  return { character: match[1], pose: match[2] };
}

// ===========================================
// SORTABLE ITEM COMPONENT
// ===========================================

interface SortableProgramLevelItemProps {
  programLevel: ProgramLevel;
  onEdit: (programLevel: ProgramLevel) => void;
  onDelete: (programLevel: ProgramLevel) => void;
}

function SortableProgramLevelItem({ programLevel, onEdit, onDelete }: SortableProgramLevelItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: programLevel.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const canDelete = !programLevel.isActive;

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          mr: 2,
          color: 'text.secondary',
          '&:active': {
            cursor: 'grabbing',
          },
        }}
      >
        <DragIcon />
      </Box>
      {programLevel.color && (
        <Box
          sx={{
            width: 24,
            height: 24,
            bgcolor: programLevel.color,
            borderRadius: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            mr: 1.5,
            flexShrink: 0,
          }}
        />
      )}
      <ListItemText
        primary={programLevel.name}
        secondary={
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.25 }}>
            {programLevel.mascotEmoji && programLevel.mascotEmoji.startsWith('/') ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  component="img"
                  src={programLevel.mascotEmoji}
                  alt={programLevel.mascot || ''}
                  sx={{ width: 20, height: 20, objectFit: 'contain' }}
                />
                <Typography variant="caption" color="text.secondary" component="span">
                  {programLevel.mascot}
                </Typography>
              </Box>
            ) : programLevel.mascotEmoji ? (
              <Typography variant="caption" color="text.secondary" component="span">
                {programLevel.mascotEmoji} {programLevel.mascot}
              </Typography>
            ) : null}
            {programLevel.threshold != null && (
              <Typography variant="caption" color="text.secondary" component="span">
                Threshold: {programLevel.threshold} lessons
              </Typography>
            )}
            {programLevel.googleDriveFolder && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FolderIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" component="span">
                  {programLevel.googleDriveFolder.folderName}
                </Typography>
              </Box>
            )}
          </Box>
        }
        sx={{ flex: 1 }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Tooltip title={programLevel.isGamificationLevel ? 'Gamification active' : 'Gamification inactive'}>
          <AwardIcon fontSize="small" color={programLevel.isGamificationLevel ? 'primary' : 'disabled'} />
        </Tooltip>
        {programLevel.isActive ? (
          <Chip label="Active" size="small" color="success" />
        ) : (
          <Chip label="Inactive" size="small" color="default" />
        )}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(programLevel)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={canDelete ? 'Delete' : 'Set status to Inactive to delete this program level'}>
            <span>
              <IconButton
                size="small"
                onClick={() => canDelete && onDelete(programLevel)}
                color={canDelete ? 'error' : 'default'}
                disabled={!canDelete}
                sx={{
                  opacity: canDelete ? 1 : 0.5,
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </ListItem>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function ProgramLevelsPage() {
  const { data: programLevelsData, isLoading, error } = useProgramLevels();
  const createMutation = useCreateProgramLevel();
  const updateMutation = useUpdateProgramLevel();
  const deleteMutation = useDeleteProgramLevel();
  const reorderMutation = useReorderProgramLevels();
  const { data: driveAuthStatus } = useGoogleDriveAuthStatus();
  const isDriveConnected = driveAuthStatus?.isConnected ?? false;

  // Get program levels array from response
  const programLevels = programLevelsData ?? [];

  // Filter state
  const [hideInactive, setHideInactive] = useState(false);

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProgramLevel, setEditingProgramLevel] = useState<ProgramLevel | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    isActive: true,
    color: null as string | null,
    threshold: '' as string,
    mascot: '' as string,
    mascotEmoji: '' as string,
    isGamificationLevel: true,
  });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [programLevelToDelete, setProgramLevelToDelete] = useState<ProgramLevel | null>(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter program levels
  const filteredProgramLevels = useMemo((): ProgramLevel[] => {
    if (hideInactive) {
      return programLevels.filter((programLevel: ProgramLevel) => programLevel.isActive);
    }
    return programLevels;
  }, [programLevels, hideInactive]);

  // Handlers
  const handleAdd = () => {
    setEditingProgramLevel(null);
    setFormData({
      name: '',
      isActive: true,
      color: null,
      threshold: '',
      mascot: '',
      mascotEmoji: '',
      isGamificationLevel: true,
    });
    setModalOpen(true);
  };

  const handleEdit = (programLevel: ProgramLevel) => {
    setEditingProgramLevel(programLevel);
    setFormData({
      name: programLevel.name,
      isActive: programLevel.isActive,
      color: programLevel.color,
      threshold: programLevel.threshold != null ? String(programLevel.threshold) : '',
      mascot: programLevel.mascot || '',
      mascotEmoji: programLevel.mascotEmoji || '',
      isGamificationLevel: programLevel.isGamificationLevel,
    });
    setModalOpen(true);
  };

  const handleDelete = (programLevel: ProgramLevel) => {
    setProgramLevelToDelete(programLevel);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const data: Record<string, unknown> = {
        name: formData.name,
        isActive: formData.isActive,
        color: formData.color ?? undefined,
        threshold: formData.threshold ? parseInt(formData.threshold, 10) : null,
        mascot: formData.mascot || null,
        mascotEmoji: formData.mascotEmoji || null,
        isGamificationLevel: formData.isGamificationLevel,
      };

      if (editingProgramLevel) {
        await updateMutation.mutateAsync({
          id: editingProgramLevel.id,
          data,
        });
      } else {
        await createMutation.mutateAsync(data as Parameters<typeof createMutation.mutateAsync>[0]);
      }
      setModalOpen(false);
    } catch {
      // Error is handled by mutation
    }
  };

  const handleConfirmDelete = async () => {
    if (programLevelToDelete) {
      try {
        await deleteMutation.mutateAsync(programLevelToDelete.id);
        setDeleteDialogOpen(false);
        setProgramLevelToDelete(null);
      } catch {
        // Error is handled by mutation
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Reorder the full list, not just filtered
      const reorderedIds = arrayMove(
        programLevels.map((i: ProgramLevel) => i.id),
        programLevels.findIndex((i: ProgramLevel) => i.id === active.id),
        programLevels.findIndex((i: ProgramLevel) => i.id === over.id)
      );

      // Call the reorder API
      try {
        await reorderMutation.mutateAsync(reorderedIds);
      } catch {
        // Error is handled by mutation
      }
    }
  };

  const isFormLoading = createMutation.isPending || updateMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;

  return (
    <Box>
      <PageHeader
        title="Program Levels"
        subtitle="Manage student skill levels. Drag to reorder how program levels appear throughout the system."
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Program Levels' },
        ]}
        actionLabel="Add Program Level"
        onAction={handleAdd}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load program levels. Please try again.
        </Alert>
      )}

      {/* Filters */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2 }}
        alignItems="center"
      >
        <FormControlLabel
          control={
            <Switch
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              size="small"
            />
          }
          label="Hide inactive program levels"
        />

        {filteredProgramLevels.length !== programLevels.length && (
          <Chip
            label={`Showing ${filteredProgramLevels.length} of ${programLevels.length} program levels`}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      {/* Program Levels List */}
      <Paper sx={{ overflow: 'hidden' }}>
        {isLoading ? (
          <List>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <ListItem key={i} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ mr: 2 }}>
                  <Skeleton variant="rectangular" width={24} height={24} />
                </Box>
                <ListItemText
                  primary={<Skeleton variant="text" width="60%" />}
                />
              </ListItem>
            ))}
          </List>
        ) : filteredProgramLevels.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {programLevels.length > 0
                ? 'No program levels match the current filter.'
                : "No program levels configured. Click 'Add Program Level' to create one."}
            </Typography>
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredProgramLevels.map((i: ProgramLevel) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <List disablePadding>
                {filteredProgramLevels.map((programLevel: ProgramLevel) => (
                  <SortableProgramLevelItem
                    key={programLevel.id}
                    programLevel={programLevel}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        )}
      </Paper>

      {/* Add/Edit Modal */}
      <FormModal
        open={modalOpen}
        title={editingProgramLevel ? 'Edit Program Level' : 'Add Program Level'}
        submitLabel={editingProgramLevel ? 'Update' : 'Create'}
        loading={isFormLoading}
        onSubmit={handleSubmit}
        onClose={() => setModalOpen(false)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Program Level Name"
            placeholder="e.g., Foundation 1"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            fullWidth
          />
          <ColorPickerField
            label="Color"
            value={formData.color}
            onChange={(color) => setFormData({ ...formData, color })}
            helperText="Optional color for visual identification"
            useSchoolPalette
          />

          {/* Gamification Settings */}
          <Typography variant="subtitle2" sx={{ mt: 1, color: 'text.secondary' }}>
            Gamification Settings
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isGamificationLevel}
                onChange={(e) => setFormData({ ...formData, isGamificationLevel: e.target.checked })}
              />
            }
            label="Include in gamification system"
          />
          {formData.isGamificationLevel && (
            <>
              <TextField
                label="Lesson Threshold"
                placeholder="e.g., 40"
                value={formData.threshold}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || /^\d+$/.test(val)) {
                    setFormData({ ...formData, threshold: val });
                  }
                }}
                fullWidth
                type="text"
                inputMode="numeric"
                helperText="Number of lessons required to complete this level"
              />
              {/* Character Selection */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: -0.5 }}>
                Select Character
              </Typography>
              <Stack direction="row" spacing={1}>
                {MASCOT_CHARACTERS.map((char) => {
                  const parsed = parseAvatarPath(formData.mascotEmoji);
                  const isSelected = parsed?.character === char.folder;
                  return (
                    <Tooltip key={char.folder} title={char.name}>
                      <Box
                        onClick={() => {
                          const pose = parsed?.character === char.folder ? (parsed?.pose || 'happy') : 'happy';
                          setFormData({
                            ...formData,
                            mascot: char.name,
                            mascotEmoji: getAvatarPath(char.folder, pose),
                          });
                        }}
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 1,
                          border: '2px solid',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          bgcolor: isSelected ? 'primary.50' : 'background.paper',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <Box
                          component="img"
                          src={getAvatarPath(char.folder, 'happy')}
                          alt={char.name}
                          sx={{ width: 48, height: 48, objectFit: 'contain' }}
                        />
                      </Box>
                    </Tooltip>
                  );
                })}
              </Stack>

              {/* Pose Selection (shown after character is picked) */}
              {(() => {
                const parsed = parseAvatarPath(formData.mascotEmoji);
                if (!parsed) return null;
                const poses = getAllPoses(parsed.character);
                return (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: -0.5 }}>
                      Select Pose
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {poses.map((pose) => {
                        const isSelected = parsed.pose === pose;
                        const path = getAvatarPath(parsed.character, pose);
                        return (
                          <Tooltip key={pose} title={pose.charAt(0).toUpperCase() + pose.slice(1)}>
                            <Box
                              onClick={() => setFormData({ ...formData, mascotEmoji: path })}
                              sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 1,
                                border: '2px solid',
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                bgcolor: isSelected ? 'primary.50' : 'background.paper',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  bgcolor: 'action.hover',
                                },
                              }}
                            >
                              <Box
                                component="img"
                                src={path}
                                alt={pose}
                                sx={{ width: 40, height: 40, objectFit: 'contain' }}
                              />
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </>
                );
              })()}
            </>
          )}

          {/* Shared Resources Folder - only show when editing and Drive is connected */}
          {editingProgramLevel && isDriveConnected && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 1, color: 'text.secondary' }}>
                Shared Resources
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Link a Google Drive folder so all classes in this program level can access shared resources.
              </Typography>
              <DriveFolderPicker programLevelId={editingProgramLevel.id} />
            </>
          )}

          {editingProgramLevel && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={formData.isActive ? 'Active' : 'Inactive'}
            />
          )}
        </Box>
      </FormModal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Program Level"
        message={`Are you sure you want to delete "${programLevelToDelete?.name}"? Students assigned to this program level will need to be reassigned.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
}
